/* What a run's outcome means on screen, decided once.
 *
 * **The vocabulary is the module's whole point, and the reference design renders it as a panel rather
 * than leaving it to be inferred from colour.** Six states arrive on the wire and they are not six
 * shades of the same thing: two are refusals. `keys_disjoint` and `keys_not_unique` mean the platform
 * measured cheaply, concluded the *definition* is wrong, and declined to burn a full join against a
 * customer's database proving it row by row. Painting either red would bury the one class of outcome
 * whose fix is editing the definition rather than retrying the run — and a reader who retries a
 * refusal gets the identical refusal, which is how a screen teaches somebody that it is broken.
 *
 * Everything here is pure and takes wire types only, so the three screens share one answer to every
 * question a reader could ask twice. A tone computed inside a component is a tone that disagrees with
 * the next component to compute it.
 */

import type {
  Break,
  BreakKind,
  Definition,
  FieldTolerance,
  Investigation,
  Run,
  RunState,
  ToleranceKind,
} from '@/shared/api/generated/reconciliation';

export type RunTone = 'ok' | 'pending' | 'bad' | 'gated';

const TONES: Record<RunState, RunTone> = {
  pending: 'pending',
  running: 'pending',
  succeeded: 'ok',
  failed: 'bad',
  keys_disjoint: 'gated',
  /* Also 'gated', not 'bad': a repeated key is a refusal, not an error — the platform
   * declined because a fanned join's counts would look right and be meaningless. Same fix
   * shape as keys_disjoint: edit the definition, do not retry the run. */
  keys_not_unique: 'gated',
};

export function toneOf(state: RunState): RunTone {
  return TONES[state];
}

export function stateLabelKey(state: RunState): string {
  return `reconciliation.state.${state}`;
}

/** True for the two terminal states that are the platform declining rather than failing.
 *
 * Derived from the tone rather than listed a second time, so a seventh state cannot be a refusal here
 * and a failure there. Read by the row tint, by the outcome-vocabulary panel, and by the reason a
 * break list is empty. */
export function isRefusal(state: RunState): boolean {
  return TONES[state] === 'gated';
}

/** Whether a run is still moving. Read by the row tint and by whether the list keeps polling. */
export function isInFlight(state: RunState): boolean {
  return state === 'pending' || state === 'running';
}

/** The tint on a run's row: refusals amber, failures red, everything else the table's own ground.
 *
 * The reference tints exactly these two, and the tint is the scan rather than the information — the
 * state is also in words in its own column, because a tint is the first thing lost to a projector or
 * a low-contrast display. */
export type RowTint = 'warn' | 'error' | null;

export function rowTintOf(state: RunState): RowTint {
  if (isRefusal(state)) return 'warn';
  return state === 'failed' ? 'error' : null;
}

/** The one-line outcome an operator scans: matched, mismatched, missing.
 *
 * `null` for every non-succeeded state rather than zeros, and that is the absence-is-not-zero rule
 * this platform holds at ten sites: a refused run did not measure zero mismatches, it measured
 * nothing. Rendering three zeros would report a clean reconciliation for a join that never ran.
 *
 * Missing is `left_only + right_only` because a reader's question is "how many rows are on one side
 * only", and which side is the break list's business rather than the summary's.
 */
export interface Outcome {
  readonly matched: number;
  readonly mismatched: number;
  readonly missing: number;
}

export function outcomeOf(run: Run): Outcome | null {
  if (run.state !== 'succeeded') return null;
  return {
    matched: run.matched ?? 0,
    mismatched: run.mismatched ?? 0,
    missing: (run.left_only ?? 0) + (run.right_only ?? 0),
  };
}

/** The break count a run reported — the join's own figure, which stays exact when the list is cut.
 *
 * `null` rather than 0 when the run did not measure, for `outcomeOf`'s reason: it is the difference
 * between "this run found nothing to look at" and "nothing ran, so there is nothing to look at".
 */
export function breakTotalOf(run: Run): number | null {
  const outcome = outcomeOf(run);
  return outcome === null ? null : outcome.mismatched + outcome.missing;
}

/** Whether this run has a break list worth opening.
 *
 * Read from the *counts*, never from the state: a succeeded run that matched every row has no breaks,
 * and a link to an empty list is a dead end that looks like a fault. A truncated list still has
 * breaks — `breaks_truncated` says the list was cut, not that it is absent.
 */
export function hasBreaks(run: Run): boolean {
  const total = breakTotalOf(run);
  return total !== null && total > 0;
}

/** Why a run has no breaks to open, as four different sentences rather than one greyed link.
 *
 * The distinction is the point. "Refused, so the join never ran" and "matched every row" are both
 * empty lists and opposite facts, and a reader who cannot tell them apart cannot tell a healthy
 * reconciliation from a misconfigured one.
 */
export function noBreaksReasonKey(run: Run): string {
  if (isRefusal(run.state)) return 'reconciliation.noBreaks.refused';
  if (run.state === 'failed') return 'reconciliation.noBreaks.failed';
  if (isInFlight(run.state)) return 'reconciliation.noBreaks.inFlight';
  return 'reconciliation.noBreaks.clean';
}

/** How the key-overlap figure was produced, because the same number means two things.
 *
 * On a succeeded run it is the KMV sketch estimate at k=1024 that *preceded* the join — a few percent
 * either way above the sketch size, exact below it. On a refused run it is the estimate the gate
 * refused *on*, which is the evidence for the refusal rather than a measurement of a join that never
 * happened. The reference prints the provenance under the figure for exactly that reason.
 */
export function jaccardNoteKey(run: Run): string | null {
  if (run.jaccard_estimate === null || run.jaccard_estimate === undefined) return null;
  return isRefusal(run.state)
    ? 'reconciliation.jaccard.gate'
    : 'reconciliation.jaccard.sketch';
}

/** How many decimals the overlap figure is shown to. Three, because the sketch's own error above
 * k=1024 is a few percent — a fourth would claim a resolution the estimator does not have. */
const JACCARD_PLACES = 3;

/** The overlap figure, and it must never round up to 1.
 *
 * **Found by rendering it: `(0.9996).toFixed(3)` is `"1.000"`.** A Jaccard of 0.9996 means about one
 * key in 2,500 is on one side only, and 1.000 says the two key sets are *identical* — on the same row
 * as a run reporting 502 breaks, which is an internal contradiction on the page and the more
 * dangerous half of it is the direction: it claims agreement the run did not find. Exactly one value
 * is entitled to read as 1.000, and it is 1.
 *
 * So a value below 1 that would round to it is rendered as a floor instead — `>0.999` — which is
 * migration 0048's rule ("refuse a positive rate that would quantise to zero") applied at the other
 * end of the range, and the same device `share` uses for `<0.1%`. The mirror case is real too: a tiny
 * positive overlap must not read as `0.000`, because a refusal turns on whether the two sets share
 * *anything*.
 */
export function formatJaccard(estimate: number): string {
  if (estimate >= 1) return (1).toFixed(JACCARD_PLACES);
  if (estimate <= 0) return (0).toFixed(JACCARD_PLACES);

  const rounded = Number(estimate.toFixed(JACCARD_PLACES));
  if (rounded >= 1) return `>${(1 - 10 ** -JACCARD_PLACES).toFixed(JACCARD_PLACES)}`;
  if (rounded <= 0) return `<${(10 ** -JACCARD_PLACES).toFixed(JACCARD_PLACES)}`;
  return estimate.toFixed(JACCARD_PLACES);
}

/** A count, grouped for the reader's locale.
 *
 * **Also found by rendering it: the outcome cell read `1283402 · 469 · 33`.** A plain `{matched}` ICU
 * argument stringifies rather than formats, and a seven-digit figure with no grouping is precisely
 * the one that cannot be read at a glance — which is the whole job of that column. Grouped here
 * rather than in the message, matching `version.byte_length.toLocaleString()` on the access-policy
 * screen: the catalogue then holds a sentence and the component holds the number formatting, so a
 * translator is not handed a format skeleton to preserve.
 */
export function formatCount(value: number): string {
  return value.toLocaleString();
}

/** A field tolerance's bound, in the unit it is actually expressed in.
 *
 * Four kinds, four renderings, and three of them would be wrong for each other. `exact` has **no**
 * bound by contract, so it reads as such rather than as a zero — which would claim a numeric
 * tolerance of zero on a string column. `absolute` is the field's own unit and carries no symbol,
 * because the platform does not know whether it is rupees or seconds. `relative` is a ratio in (0, 1)
 * rendered as a percentage, which is the one place a number is transformed: the substrate refuses a
 * bound at or above 1 precisely so a 5 typed for 5% is a refusal rather than a silent hundredfold
 * widening, and printing the stored 0.05 would make a reader do that conversion in their head every
 * time. `fuzzy` is a similarity threshold and reads as a floor, because it is one.
 */
export interface BoundReading {
  readonly key: string;
  readonly values: Readonly<Record<string, string>>;
}

export function boundReading(field: FieldTolerance): BoundReading {
  const bound = field.bound;
  if (field.kind === 'exact' || bound === null || bound === undefined) {
    return { key: 'reconciliation.bound.exact', values: {} };
  }
  if (field.kind === 'relative') {
    return {
      key: 'reconciliation.bound.relative',
      values: { percent: formatRatioAsPercent(bound) },
    };
  }
  if (field.kind === 'fuzzy') {
    return { key: 'reconciliation.bound.fuzzy', values: { bound: String(bound) } };
  }
  return { key: 'reconciliation.bound.absolute', values: { bound: String(bound) } };
}

/** A ratio as a percentage with no trailing zeros, so 0.001 reads as 0.1% and not 0.10%.
 *
 * Three decimal places below a tenth of a percent and two above, which is the resolution the
 * substrate's own (0, 1) range makes meaningful: 0.0005 is 0.05% and would round to nothing at two
 * places, while an ordinary 1% bound must not render as 1.00%. `toFixed` rather than
 * `Intl.NumberFormat` because this is a *bound somebody entered* rather than a measurement — it has
 * to read back as the number they typed.
 */
export function formatRatioAsPercent(ratio: number): string {
  const scaled = ratio * 100;
  const fixed = scaled.toFixed(Math.abs(scaled) < 0.1 ? 3 : 2);
  return fixed.includes('.') ? fixed.replace(/0+$/u, '').replace(/\.$/u, '') : fixed;
}

/** The Tolerances cell on the definitions list: how many fields, at which version, of which kinds.
 *
 * A count per kind rather than the kinds themselves, because the cell has one line and a definition
 * with nine fields would wrap into the row below it. The panel on the runs screen carries the full
 * per-field table; this is the scan that tells a reader whether to open it.
 */
export interface ToleranceSummary {
  readonly fieldCount: number;
  readonly version: number;
  /** `[kind, count]` in the enum's own order, so two definitions with the same mix read identically. */
  readonly byKind: readonly (readonly [ToleranceKind, number])[];
}

const KIND_ORDER: readonly ToleranceKind[] = ['exact', 'absolute', 'relative', 'fuzzy'];

export function toleranceSummary(definition: Definition): ToleranceSummary | null {
  /* Null rather than a zero-field summary, and the difference is a fact about the API: the list
   * response omits `tolerance_version` by contract, so absence here means "not read on this route"
   * rather than "this definition has no tolerances" — which is a state the substrate refuses to
   * store, since a definition and its first tolerance version are created in one act. A summary
   * reading "0 fields, v0" would be a claim nothing measured. */
  const version = definition.tolerance_version;
  if (version === null) return null;

  const counts = new Map<ToleranceKind, number>();
  for (const field of version.fields) {
    counts.set(field.kind, (counts.get(field.kind) ?? 0) + 1);
  }
  return {
    fieldCount: version.fields.length,
    version: version.version,
    byKind: KIND_ORDER.filter((kind) => counts.has(kind)).map(
      (kind) => [kind, counts.get(kind) ?? 0] as const,
    ),
  };
}

export type BreakTone = 'warn' | 'left' | 'right';

/* Three kinds, three hues, as the design draws them. A mismatch is amber: both sides have the row and
 * disagree about a field, by an amount the delta states. A row on one side only is not a severity but
 * a direction, so the two sides get two quiet hues rather than one alarm — the reader's question is
 * which side is missing rows, and one red for both would hide the answer.
 */
const BREAK_TONES = {
  mismatch: 'warn',
  left_only: 'left',
  right_only: 'right',
} satisfies Record<BreakKind, BreakTone>;

export function breakToneOf(kind: BreakKind): BreakTone {
  return BREAK_TONES[kind];
}

export function breakKindLabelKey(kind: BreakKind): string {
  return `reconciliation.break.${kind}`;
}

/** The break-kind filter: `all` plus the three real kinds, in the reference's own order. */
export type BreakFilter = 'all' | BreakKind;

export const BREAK_FILTERS: readonly BreakFilter[] = [
  'all',
  'mismatch',
  'left_only',
  'right_only',
];

export function filterBreaks(
  breaks: readonly Break[],
  filter: BreakFilter,
): readonly Break[] {
  return filter === 'all' ? breaks : breaks.filter((item) => item.kind === filter);
}

/** How many of each kind are in the *loaded* list, which is what the filter chips can honestly count.
 *
 * Deliberately not the run's own totals. `breaks_truncated` means the list is a sample while the
 * counts on the run stay exact, so a chip reading "Mismatch (469)" over a list of five rows would
 * promise 469 rows a click cannot produce. The truncation band above the table carries the real
 * figures; these count what is here.
 */
export function breakCounts(
  breaks: readonly Break[],
): Readonly<Record<BreakKind, number>> {
  const counts = { mismatch: 0, left_only: 0, right_only: 0 };
  for (const item of breaks) counts[item.kind] += 1;
  return counts;
}

/** The delta cell: signed, or absent.
 *
 * `null` is rendered by the caller as an em dash and never as a zero, because a missing row and a
 * string disagreement genuinely have no delta — the field is not numeric, or there is no second value
 * to subtract from. A zero would say the two sides agreed exactly, on a row that is in the break list
 * precisely because they did not.
 *
 * A leading `+` on a positive value, because the figure is left minus right: the sign is the direction
 * of the disagreement and is half the information, and an unsigned 12.5 beside a -6.75 reads as two
 * magnitudes rather than as two directions.
 */
export function formatDelta(delta: number | null): string | null {
  if (delta === null) return null;
  const magnitude = delta.toLocaleString(undefined, { maximumFractionDigits: 6 });
  return delta > 0 ? `+${magnitude}` : magnitude;
}

/** The loaded breaks as a CSV blob, in the order and selection the screen is showing.
 *
 * **A real file of exactly what the screen has, and deliberately not "export the full set".** No
 * export endpoint exists on this API, and a button claiming to queue one would be the
 * claim-without-mechanism failure CLAUDE.md §9 records more often than any other — the design's own
 * mockup says "an export of all 502 breaks was queued", which is a sentence no code here can make
 * true. What this does is honest and is the thing somebody actually wants: the masked rows in front
 * of them, to take to the team that owns the upstream table. When the run reports the list was cut,
 * the caller says so beside the download rather than here.
 *
 * Every field written is already masked — a digest, a preview the source rendered, field *names*, a
 * distance — so the file cannot carry a value the screen does not have. That is a property of the API
 * rather than of this function: `Break` has no field a value would fit in.
 */

/** CRLF, because the one thing anybody does with this file is open it in a spreadsheet. */
const CSV_LINE_END = '\r\n';

export function breaksToCsv(breaks: readonly Break[]): string {
  const header = ['kind', 'masked_key', 'key_digest', 'fields', 'numeric_delta'];
  const rows = breaks.map((item) => [
    item.kind,
    item.masked_key,
    item.key_digest,
    item.fields.join(' '),
    item.numeric_delta === null ? '' : String(item.numeric_delta),
  ]);
  return [header, ...rows]
    .map((cells) => cells.map(quoteCsvCell).join(','))
    .join(CSV_LINE_END);
}

/* Quoted unconditionally rather than only when needed. A masked key is source-rendered text and a
 * field name is a column identifier: neither is guaranteed free of a comma or a quote, and a
 * conditional quote is one more branch to get wrong on a file somebody opens in a spreadsheet. */
function quoteCsvCell(value: string): string {
  return `"${value.replace(/"/gu, '""')}"`;
}

export function orderRuns(runs: readonly Run[]): Run[] {
  return [...runs].sort((left, right) =>
    right.requested_at.localeCompare(left.requested_at),
  );
}

/** One side of a definition, as the runs header's subline reads it: the table and its key columns. */
export function sideLabel(qualifiedName: string, keyColumns: readonly string[]): string {
  return `${qualifiedName} (${keyColumns.join(', ')})`;
}

/* ------------------------------------------------------------------ the board's search and filters */

/** Matching is on the name and on both sides' qualified names.
 *
 * **The sides are searchable and not only the name**, because the question somebody arrives with is
 * usually "what compares `MART.FCT_REPAYMENT`" rather than "what is the definition called" — a
 * reconciliation is named by whoever authored it and a table is named by the estate.
 */
export function matchesSearch(definition: Definition, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (needle === '') return true;
  return [
    definition.name,
    definition.left.qualified_name,
    definition.right.qualified_name,
  ].some((field) => field.toLowerCase().includes(needle));
}

/** The board's quick filters, which are the three questions a steward opens this screen with.
 *
 * Each is a *state of the estate* rather than a state of one definition, which is why they are here
 * and not a column sort: "which of these declined" and "which of these has nobody scheduled" are the
 * two ways a reconciliation quietly stops being a control.
 */
export type BoardFilter = 'all' | 'refused' | 'never_run';

export const BOARD_FILTERS: readonly BoardFilter[] = ['all', 'never_run', 'refused'];

/** Whether a definition belongs under a filter, judged from its most recent run.
 *
 * `never_run` is an *absence* of runs rather than a run in some state, and it is the filter that
 * finds a definition somebody authored and nobody has used — which looks identical to a healthy one
 * on every other column.
 */
export function matchesBoardFilter(
  filter: BoardFilter,
  latest: Run | undefined,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'never_run') return latest === undefined;
  /* "Last run refused" covers a run that failed as well as one refused for its keys, as the design
     has it: in all three the last attempt produced no comparison, which is what the filter finds. */
  return latest !== undefined && (isRefusal(latest.state) || latest.state === 'failed');
}

/** The scopes the panel offers, in the order it offers them. `mismatched` first: it is what somebody
 * opens the page for, and the default. */
export const INVESTIGATION_SCOPE_ORDER = ['mismatched', 'missing', 'all'] as const;

/** Which side's connection a statement belongs to, as a title the reader recognises.
 *
 * `both` is one statement over two tables on one connection; `left` and `right` are the two halves of
 * a cross-connection comparison, which the platform will not join. Named source and target — the
 * words the reconciliation screens use — rather than left and right.
 */
export function sideTitleKey(side: string): string {
  switch (side) {
    case 'left':
      return 'reconciliation.investigation.side.left';
    case 'right':
      return 'reconciliation.investigation.side.right';
    default:
      return 'reconciliation.investigation.side.both';
  }
}

/** Every statement as one `.sql` file, each preceded by where to run it.
 *
 * The statements already carry their own comment header (run, kind, connection); this adds a banner
 * and a separator so a file holding four of them reads as four. Plain text only — the same values the
 * panel shows, and no more.
 */
export function investigationToSql(
  investigation: Pick<Investigation, 'queries'>,
  banner: string,
): string {
  const parts = investigation.queries.map((query) => query.sql.trimEnd());
  return [`-- ${banner}`, ...parts].join('\n\n');
}
